/**
 * Page Loader Component
 * ページの読み込み状態を管理し、フェードインアニメーションとの連携を行う
 */

class PageLoader {
  constructor() {
    this.isLoading = true;
    this.loadStartTime = Date.now();
    this.minLoadTime = 500; // 最小読み込み時間（ms）

    this.init();
  }

  init() {
    // bodyにローディングクラスを追加
    document.body.classList.add('is-loading');

    // ページの読み込み完了を監視
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.handleDOMLoaded.bind(this));
    } else {
      this.handleDOMLoaded();
    }

    // すべてのリソースの読み込み完了を監視
    if (document.readyState !== 'complete') {
      window.addEventListener('load', this.handleWindowLoad.bind(this));
    } else {
      this.handleWindowLoad();
    }
  }

  handleDOMLoaded() {
    // DOM構築完了
    this.checkLoadComplete();
  }

  handleWindowLoad() {
    // 全リソース読み込み完了
    this.checkLoadComplete();
  }

  checkLoadComplete() {
    if (!this.isLoading) return;

    const currentTime = Date.now();
    const elapsedTime = currentTime - this.loadStartTime;
    const remainingTime = Math.max(0, this.minLoadTime - elapsedTime);

    // 最小読み込み時間を確保してからローディング完了
    setTimeout(() => {
      this.completeLoading();
    }, remainingTime);
  }

  completeLoading() {
    if (!this.isLoading) return;

    this.isLoading = false;

    // ローディングクラスを削除
    document.body.classList.remove('is-loading');

    // カスタムイベントを発火（他のコンポーネントで利用可能）
    const loadCompleteEvent = new CustomEvent('pageLoadComplete', {
      detail: {
        loadTime: Date.now() - this.loadStartTime
      }
    });
    document.dispatchEvent(loadCompleteEvent);

    // フェードインアニメーションが存在する場合は連携
    if (window.fadeInAnimation) {
      // 少し遅延してからアニメーション開始
      setTimeout(() => {
        this.triggerInitialAnimations();
      }, 100);
    }
  }

  triggerInitialAnimations() {
    // 初期表示されるべき要素のアニメーションを即座に実行
    const immediateElements = document.querySelectorAll('.p-header, .p-mv');
    immediateElements.forEach(element => {
      if (element.style.opacity === '0') {
        element.style.opacity = '1';
        element.style.translate = '0 0';
      }
    });
  }
}

// フォント読み込み管理
class FontLoader {
  constructor() {
    this.fontsToLoad = [
      'Noto Sans JP',
      'Zen Kurenaido'
    ];

    this.init();
  }

  init() {
    // Font Loading API をサポートしている場合
    if ('fonts' in document) {
      this.loadFontsWithAPI();
    } else {
      // フォールバック
      this.loadFontsWithFallback();
    }
  }

  async loadFontsWithAPI() {
    try {
      // フォントの読み込みを待機
      await document.fonts.ready;

      // カスタムイベントを発火
      const fontsLoadedEvent = new CustomEvent('fontsLoaded');
      document.dispatchEvent(fontsLoadedEvent);

    } catch (error) {
      // フォント読み込み失敗時はフォールバックを使用
      this.loadFontsWithFallback();
    }
  }

  loadFontsWithFallback() {
    // フォールバック：タイムアウト後にフォント読み込み完了とみなす
    setTimeout(() => {
      const fontsLoadedEvent = new CustomEvent('fontsLoaded');
      document.dispatchEvent(fontsLoadedEvent);
    }, 3000);
  }
}

// 画像の遅延読み込み（パフォーマンス向上）
class LazyImageLoader {
  constructor() {
    this.images = [];
    this.options = {
      rootMargin: '50px 0px',
      threshold: 0.01
    };

    this.init();
  }

  init() {
    // data-src属性を持つ画像を取得
    this.images = document.querySelectorAll('img[data-src]');

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), this.options);
      this.observeImages();
    } else {
      // フォールバック：即座に全て読み込み
      this.loadAllImages();
    }
  }

  observeImages() {
    this.images.forEach(img => {
      this.observer.observe(img);
    });
  }

  handleIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.loadImage(entry.target);
        this.observer.unobserve(entry.target);
      }
    });
  }

  loadImage(img) {
    const src = img.getAttribute('data-src');
    if (src) {
      img.src = src;
      img.removeAttribute('data-src');
      img.classList.add('loaded');
    }
  }

  loadAllImages() {
    this.images.forEach(img => {
      this.loadImage(img);
    });
  }
}

// 初期化
document.addEventListener('DOMContentLoaded', () => {
  // ページローダーを初期化
  window.pageLoader = new PageLoader();

  // フォントローダーを初期化
  window.fontLoader = new FontLoader();

  // 遅延画像読み込みを初期化
  window.lazyImageLoader = new LazyImageLoader();
});

// パフォーマンス監視（開発用）
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('load', () => {
    // パフォーマンス情報を取得（開発環境でのみ実行）
    const perfData = performance.getEntriesByType('navigation')[0];
    if (perfData) {
      // パフォーマンス情報を内部で保持
      window.performanceData = {
        domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
        loadComplete: perfData.loadEventEnd - perfData.loadEventStart,
        totalTime: perfData.loadEventEnd - perfData.navigationStart
      };
    }
  });
}